import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  Renderer2,
  ViewChild
} from '@angular/core';
import { BusinessFilter } from 'src/app/models/register-business';
import { listingViewport } from 'src/app/common/listing-viewport';
import { Subject } from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { BusinessService } from 'src/app/services/business.service';
import { PageTitleService } from 'src/app/services/page-title.service';
import { TourService } from 'src/app/tour/tour.service';
import { PinWindow, RestaurantDetailSectionComponent } from 'src/app/shared/components/restaurant-detail-section/restaurant-detail-section.component';

@Component({
  selector: 'app-listing',
  templateUrl: './listing.component.html',
  styleUrls: ['./listing.component.scss'],
})
export class ListingComponent implements AfterViewInit, OnDestroy {
  @ViewChild(RestaurantDetailSectionComponent) restaurantSection?: RestaurantDetailSectionComponent;
  private destroy$ = new Subject<void>();
  activePanel = 1;
  active = 'place';
  activeInner = 'type-of-meal';
  isSearchBarActive = false;
  // The document title follows the place being browsed; a setter catches every
  // assignment (restored state, saved location, search bar emission).
  private _city: any;
  get city(): any {
    return this._city;
  }
  set city(value: any) {
    this._city = value;
    const place = (value?.city ?? '').toString().trim();
    this.pageTitle.set(place ? `Restaurants in ${place}` : 'Restaurants');
  }
  resturantCoordinates: any;
  /** Batches the list is reading; the map shows only their pins. */
  pinWindow: PinWindow | null = null;
  filter: BusinessFilter = new BusinessFilter();
  currentCenter: any;
  isInitial: boolean = true;
  searchIds: any[] = [];
  isLoading: boolean = false;
  locationUnavailable = false;
  @ViewChild('listingSearch') private listingSearch?: {
    onLocationSelected(location: { lat: number; lng: number; address: string }): void;
  };
  private locationRetryTimer?: ReturnType<typeof setTimeout>;
  private locationRetryCount = 0;
  private locationRequestRevision = 0;
  private destroyed = false;
  private restoredFromReturnNavigation: boolean = false;
  private tourListing = false;
  tourLocation: { lat: number; lng: number; label: string } | null = null;

  // Mobile: the first swipe slides the sheet over the fixed map. At the map's floor the
  // inner list takes over, filling the space down to the visible viewport bottom.
  @ViewChild('listSheet') private listSheet?: ElementRef<HTMLElement>;
  private sheetFloorTop = NaN;
  private sheetIsFloored = false;
  private sheetResizeObserver?: ResizeObserver;
  private sheetContentObserver?: MutationObserver;
  private sheetMeasureFrame?: number;
  private readonly syncSheetFloor = () => this.scheduleSheetMeasure();
  private readonly remeasureSheetFloor = () => {
    this.measureSheetFloor();
    this.measureListScrollSpace();
    this.applySheetFloorState();
  };
  private readonly scheduleSheetMeasure = () => {
    if (this.destroyed || this.sheetMeasureFrame != null) return;
    this.sheetMeasureFrame = requestAnimationFrame(() => {
      this.sheetMeasureFrame = undefined;
      this.remeasureSheetFloor();
    });
  };

  constructor(
    private elRef: ElementRef,
    private userService: UserService,
    private businessService: BusinessService,
    private zone: NgZone,
    private renderer: Renderer2,
    private pageTitle: PageTitleService,
    private tour: TourService,
  ) { }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: Event) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isSearchBarActive = false;
    }
  }

  @HostListener('window:beforeunload')
  handleBeforeUnload() {
    this.persistListingViewState();
  }

  // Toggle View Function
  gridView: boolean = true;

  toggleGridView() {
    this.gridView = true;
  }

  toggleLineView() {
    this.gridView = false;
  }

  ngOnInit(): void {
    // Marks the screen so the footer can be dropped on mobile: anything after the sheet would
    // add page scroll past the map's floor, and it sits behind the opaque sheet regardless.
    this.renderer.addClass(document.body, 'listing-page-body');

    if (this.tour.active) {
      this.tour.snapshotListing(this.originalListingSnapshot());
      this.tourListing = true;
      // Browser Back delivers NavigationEnd after child views initialize. Seed the
      // scoped location now so those views never start an ordinary GPS request.
      this.applyTourLocation();
      return;
    }

    const tourSnapshot = this.tour.restoreListingSnapshot();
    if (tourSnapshot?.filterDataObject?.filter) {
      this.businessService.filterDataObject = tourSnapshot.filterDataObject;
      this.activePanel = tourSnapshot.activePanel ?? this.activePanel;
      this.gridView = tourSnapshot.gridView ?? this.gridView;
      this.currentCenter = tourSnapshot.currentCenter || null;
      this.businessService.setCloseOpenValue({
        showOpen: tourSnapshot.filterDataObject.showOpen === true,
        showClose: tourSnapshot.filterDataObject.showClose === true
      });
    }

    const restored = this.restoreListingViewState();
    if (!restored) {
      const dashboardFilter = this.businessService.filterDataObject?.filter;
      const hasDashboardLocation = dashboardFilter?.origin_Lat != null && dashboardFilter?.origin_long != null;
      const hasDashboardSearch = dashboardFilter?.search && dashboardFilter.search.length >= 3;
      if (!this.businessService.hasListingReturnPending() && (hasDashboardSearch || hasDashboardLocation)) {
        this.filter = Object.assign({}, dashboardFilter);
        const dashboardCity = this.businessService.filterDataObject?.city;
        if (dashboardCity && hasDashboardLocation) {
          this.city = {
            city: dashboardCity,
            lat: dashboardFilter.origin_Lat,
            lng: dashboardFilter.origin_long,
            enableZoomFallBack: true
          };
        }
        this.isInitial = false;
        this.isLoading = false;
        return;
      }
      if (!this.businessService.hasListingReturnPending()) {
        this.businessService.clearListingState();
      }
      this.loadDeviceLocation();
    }
  }

  private loadDeviceLocation(onReady?: (position: { lat: number; lng: number }) => void): void {
    this.clearLocationRetry();
    const revision = ++this.locationRequestRevision;
    this.isLoading = true;
    this.locationUnavailable = false;
    this.userService.getCurrentLatLng().then(position => {
      if (this.destroyed || revision !== this.locationRequestRevision || !this.isLoading) return;
      this.isLoading = false;
      this.locationRetryCount = 0;
      this.filter = { ...this.filter, origin_Lat: String(position.lat), origin_long: String(position.lng) };
      onReady?.(position);
    }, error => {
      if (this.destroyed || revision !== this.locationRequestRevision || !this.isLoading) return;
      this.isLoading = false;
      this.locationUnavailable = true;
      // Retry temporary GPS failures quietly. Permission denial requires the browser's
      // grant; recovery stays in the existing location picker, never a second page CTA.
      if (error?.code === 1 || this.locationRetryCount >= 2) return;
      const delay = [1000, 3000][this.locationRetryCount++];
      this.locationRetryTimer = setTimeout(() => {
        this.locationRetryTimer = undefined;
        if (this.destroyed || !this.locationUnavailable || revision !== this.locationRequestRevision) return;
        this.loadDeviceLocation(onReady || (position => this.listingSearch?.onLocationSelected({
          ...position, address: 'Current location'
        })));
      }, delay);
    });
  }

  private clearLocationRetry(): void {
    if (this.locationRetryTimer != null) clearTimeout(this.locationRetryTimer);
    this.locationRetryTimer = undefined;
  }

  private cancelDeviceLocationRecovery(): void {
    this.clearLocationRetry();
    this.locationRequestRevision++;
  }

  private originalListingSnapshot(): any {
    const stored = this.businessService.hydrateListingState();
    const context = this.businessService.filterDataObject || stored?.filterDataObject;
    if (!context?.filter) return {};
    const keys = ['offset', 'limit', 'search', 'sortingType', 'FilterByRating', 'FilterByPriceRange',
      'FilterByMealType', 'FilterByCategories', 'origin_Lat', 'origin_long', 'timeZoneOffset',
      'isOpen', 'zoomLevel', 'north', 'south', 'east', 'west', 'enableZoomFallBack', 'isForTest'];
    const filter = Object.fromEntries(Object.entries(context.filter).filter(([key, value]) =>
      keys.includes(key) && (value == null || ['string', 'number', 'boolean'].includes(typeof value))));
    const scalarArray = (value: any) => Array.isArray(value)
      ? Array.from(value, item => ['string', 'number', 'boolean'].includes(typeof item) ? item : false) : [];
    const selected = context.selectedLocation;
    const center = stored?.currentCenter;
    return {
      filterDataObject: {
        filter, city: typeof context.city === 'string' ? context.city : '',
        selectedLocation: selected ? { lat: Number(selected.lat), lng: Number(selected.lng), address: String(selected.address || '') } : null,
        selectedPrices: scalarArray(context.selectedPrices), selectedCategories: scalarArray(context.selectedCategories),
        selectedTypeOfMeals: scalarArray(context.selectedTypeOfMeals),
        displayFilterChips: Array.isArray(context.displayFilterChips) ? context.displayFilterChips.map(chip => ({
          id: Number(chip.id), name: String(chip.name || ''), isArticle: chip.isArticle === true, type: Number(chip.type)
        })) : [],
        showOpen: context.showOpen === true || stored?.listMeta?.showOpen === true,
        showClose: context.showClose === true || stored?.listMeta?.showClose === true
      },
      activePanel: typeof stored?.activePanel === 'number' ? stored.activePanel : 1,
      gridView: typeof stored?.gridView === 'boolean' ? stored.gridView : true,
      currentCenter: center ? { lat: Number(center.lat), lng: Number(center.lng), zoom: Number(center.zoom) } : null
    };
  }

  private applyTourLocation() {
    const location = this.tour.location;
    this.tourLocation = { ...location };
    this.city = { city: location.label, lat: location.lat, lng: location.lng, enableZoomFallBack: true };
    this.filter = Object.assign(new BusinessFilter(), {
      search: '', origin_Lat: String(location.lat), origin_long: String(location.lng),
      zoomLevel: 14, north: String(location.lat + 0.045), south: String(location.lat - 0.045),
      east: String(location.lng + 0.05), west: String(location.lng - 0.05),
      enableZoomFallBack: true
    });
    this.currentCenter = { lat: location.lat, lng: location.lng, zoom: 14, enableZoomFallBack: true };
    this.searchIds = [];
    this.activePanel = 1;
    this.isInitial = false;
    this.isLoading = false;
  }

  updateResturantCoordinates(event) {
    // Only update when the marker set actually changed. Identity + order of
    // (restaurantId, lat, lng) is what the map consumes; the old code serialised
    // both full lists with JSON.stringify on every load and then deep-cloned.
    // A fresh array reference is still assigned so OnPush children re-render.
    if (!this.sameCoordinateSets(this.resturantCoordinates, event)) {
      this.resturantCoordinates = Array.isArray(event) ? [...event] : event;
    }
    this.persistListingViewState();
  }

  private sameCoordinateSets(a: any, b: any): boolean {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i]?.restaurantId !== b[i]?.restaurantId ||
          a[i]?.lat !== b[i]?.lat ||
          a[i]?.lng !== b[i]?.lng ||
          a[i]?.listingIndex !== b[i]?.listingIndex) {
        return false;
      }
    }
    return true;
  }

  filterChanges(filter: BusinessFilter) {
    if (this.city && !listingViewport(filter) && (!this.currentCenter || this.currentCenter?.lat == null || this.currentCenter?.lng == null)) {
      filter.origin_Lat = this.city?.lat;
      filter.origin_long = this.city?.lng;
      filter.enableZoomFallBack = this.city?.enableZoomFallBack;
    }
    if (filter?.search?.trim().length >= 3) {
      this.cancelDeviceLocationRecovery();
      this.isLoading = false;
      this.locationUnavailable = false;
    }
    if (this.isInitial) {
      this.isInitial = false;
    }
    if ((filter?.origin_Lat == null || filter?.origin_Lat == undefined) && (filter?.origin_long == null || filter?.origin_long == undefined)) {
      filter.origin_Lat = this.filter.origin_Lat;
      filter.origin_long = this.filter.origin_long;
    }
    this.filter = Object.assign({}, filter);
    if (listingViewport(filter)) {
      this.currentCenter = { lat: Number(filter.origin_Lat), lng: Number(filter.origin_long), zoom: filter.zoomLevel,
        north: filter.north, south: filter.south, east: filter.east, west: filter.west,
        enableZoomFallBack: filter.enableZoomFallBack };
    }
    this.persistListingViewState();
    if (this.currentCenter && this.currentCenter.zoom) {
      if (this.currentCenter.lat !== this.filter.origin_Lat || this.currentCenter.lng !== this.filter.origin_long) {
        this.currentCenter.lat = this.filter.origin_Lat;
        this.currentCenter.lng = this.filter.origin_long;
      }
      this.currentCenter.zoom = this.filter.zoomLevel;
      this.currentCenter.north = this.filter.north;
      this.currentCenter.south = this.filter.south;
      this.currentCenter.east = this.filter.east;
      this.currentCenter.west = this.filter.west;
      this.currentCenter.enableZoomFallBack = this.filter.enableZoomFallBack;
    }
  }

  cityEmitted($event) {
    if ($event?.lat != null && $event?.lng != null) {
      this.cancelDeviceLocationRecovery();
      this.isLoading = false;
      this.locationUnavailable = false;
    }
    if (this.city?.lat !== $event?.lat || this.city?.lng !== $event?.lng) {
      this.city = $event;
      this.filter.origin_Lat = this.city?.lat;
      this.filter.origin_long = this.city?.lng;
      this.filter.enableZoomFallBack = this.city?.enableZoomFallBack;
      // Flat scalars; the spread gives the OnPush child a new reference without serialising.
      this.filter = { ...this.filter };
    }
    this.persistListingViewState();
  }

  onCenterChange($event) {
    this.cancelDeviceLocationRecovery();
    this.locationUnavailable = false;
    this.isLoading = false;
    this.currentCenter = $event;
    this.persistListingViewState();
  }

  showSearchChanges($event) {
    this.searchIds = $event;
    if (Array.isArray(this.searchIds) && this.searchIds.length > 0 && this.filter?.search) {
      this.filter = Object.assign({}, this.filter, { search: '' });
    }
    this.persistListingViewState();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.cancelDeviceLocationRecovery();
    this.renderer.removeClass(document.body, 'listing-page-body');
    window.removeEventListener('scroll', this.syncSheetFloor);
    window.removeEventListener('resize', this.remeasureSheetFloor);
    window.visualViewport?.removeEventListener('resize', this.scheduleSheetMeasure);
    this.sheetResizeObserver?.disconnect();
    this.sheetContentObserver?.disconnect();
    if (this.sheetMeasureFrame != null) cancelAnimationFrame(this.sheetMeasureFrame);
    this.persistListingViewState();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Viewport position the sheet comes to rest at. NaN on desktop, where the whole mechanism
   *  is off and the page scrolls normally. */
  private measureSheetFloor() {
    if (typeof window === 'undefined' || window.innerWidth >= 768) {
      this.sheetFloorTop = NaN;
      this.listSheet?.nativeElement.classList.remove('is-floored');
      this.sheetIsFloored = false;
      return;
    }

    const root = getComputedStyle(document.documentElement);
    const header = parseFloat(root.getPropertyValue('--hungery-header-offset'));
    const floor = parseFloat(root.getPropertyValue('--hungery-listing-map-floor'));
    this.sheetFloorTop = (isFinite(header) ? header : 68) + (isFinite(floor) ? floor : 150);
  }

  private applySheetFloorState() {
    const el = this.listSheet?.nativeElement;
    if (!el || !isFinite(this.sheetFloorTop)) {
      return;
    }

    const floored = el.getBoundingClientRect().top <= this.sheetFloorTop + 1;
    if (floored !== this.sheetIsFloored) {
      this.sheetIsFloored = floored;
      el.classList.toggle('is-floored', floored);
    }
  }

  private measureListScrollSpace(): void {
    const sheet = this.listSheet?.nativeElement;
    if (!sheet) return;
    if (window.innerWidth >= 768) {
      for (const property of ['--hungery-list-visible-height', '--hungery-list-end-clearance', '--hungery-list-viewport-height']) {
        sheet.style.removeProperty(property);
      }
      return;
    }
    const list = sheet.querySelector<HTMLElement>('.restro-details-wrap');
    if (!list) return;
    const viewport = window.visualViewport;
    // Keyboard/pinch changes belong to the location editor, not to the list's scroll stops.
    if (viewport && (viewport.scale !== 1 ||
        (document.activeElement?.matches('input, textarea, [contenteditable="true"]') && viewport.height < window.innerHeight * 0.75))) return;
    const viewportHeight = viewport?.height || window.innerHeight;
    // The list grows as the sheet covers the map. Use its current visible position;
    // adding document scroll here would leave the original map travel as blank space.
    // The list's own scrollTop does not move its bounding rectangle.
    const listStart = list.getBoundingClientRect().top;
    const viewportBottom = (viewport?.offsetTop || 0) + viewportHeight;
    const bottomGap = 12;
    const listHeight = Math.max(0, viewportBottom - listStart - bottomGap);
    const rootStyle = getComputedStyle(document.documentElement);
    const buttonClearance = parseFloat(rootStyle.getPropertyValue('--hungery-mobile-filter-btn-clearance')) || 62;
    const filterGap = parseFloat(rootStyle.getPropertyValue('--hungery-mobile-filter-gap')) || 16;
    // Keep space for the closed 32px pill row even while its filter panel is open.
    let controlsHeight = buttonClearance + filterGap + 32;
    for (const control of this.elRef.nativeElement.querySelectorAll('.hungery-mobile-search-wrap > .form-group, .mobile-active-filters')) {
      const rect = (control as HTMLElement).getBoundingClientRect();
      if (rect.width && rect.height) controlsHeight = Math.max(controlsHeight, viewportBottom - rect.top);
    }
    sheet.style.setProperty('--hungery-list-viewport-height', `${viewportHeight}px`);
    sheet.style.setProperty('--hungery-list-visible-height', `${listHeight}px`);
    sheet.style.setProperty('--hungery-list-end-clearance', `${Math.ceil(controlsHeight + 16)}px`);
  }

  ngAfterViewInit(): void {
    // Outside Angular, passive and once per animation frame: scroll geometry does not
    // need Angular change detection on every scroll tick.
    this.zone.runOutsideAngular(() => {
      this.remeasureSheetFloor();
      window.addEventListener('scroll', this.syncSheetFloor, { passive: true });
      window.addEventListener('resize', this.remeasureSheetFloor, { passive: true });
      window.visualViewport?.addEventListener('resize', this.scheduleSheetMeasure, { passive: true });
      if (this.listSheet) {
        this.sheetResizeObserver = new ResizeObserver(this.scheduleSheetMeasure);
        this.sheetResizeObserver.observe(this.listSheet.nativeElement);
        const controls = this.elRef.nativeElement.querySelector('.hungery-mobile-search-wrap');
        if (controls) this.sheetResizeObserver.observe(controls);
        this.sheetContentObserver = new MutationObserver(this.scheduleSheetMeasure);
        this.sheetContentObserver.observe(this.listSheet.nativeElement, { childList: true, subtree: true });
        const page = this.elRef.nativeElement.querySelector('.listing-page-wrapper');
        if (page) this.sheetContentObserver.observe(page, { childList: true });
      }
    });

    if (this.tourListing) return;

    if (this.restoredFromReturnNavigation) {
      setTimeout(() => {
        this.businessService.clearListingReturnPending();
        this.restoredFromReturnNavigation = false;
      });
      return;
    }
    setTimeout(() => {
      if (!this.businessService.hasListingReturnPending()) {
        this.businessService.filterDataObject = null;
      }
    });
  }

  private restoreListingViewState(): boolean {
    const dashboardFilter = this.businessService.filterDataObject?.filter;
    const hasDashboardLocation = dashboardFilter?.origin_Lat != null && dashboardFilter?.origin_long != null;
    const hasDashboardSearch = !!dashboardFilter?.search;
    if (!this.businessService.hasListingReturnPending() && (hasDashboardSearch || hasDashboardLocation)) {
      return false;
    }
    if (!this.businessService.hasListingReturnPending()) {
      return false;
    }
    const storedState = this.businessService.hydrateListingState();
    if (!storedState) return false;

    if (typeof storedState.activePanel === 'number') {
      this.activePanel = storedState.activePanel;
    }
    if (typeof storedState.gridView === 'boolean') {
      this.gridView = storedState.gridView;
    }
    if (storedState.currentCenter) {
      this.currentCenter = storedState.currentCenter;
    }
    if (Array.isArray(storedState.searchIds)) {
      this.searchIds = storedState.searchIds;
    }
    const hasSearchIds = Array.isArray(this.searchIds) && this.searchIds.length > 0;
    const hasFilters = this.businessService.shouldRestoreListingState(storedState);
    if (hasFilters && Array.isArray(storedState.resturantCoordinates)) {
      this.resturantCoordinates = structuredClone(storedState.resturantCoordinates);
    }
    if (hasFilters) {
      if (storedState.filterDataObject?.filter) {
        const restoredFilter = Object.assign({}, storedState.filterDataObject.filter);
        if (hasSearchIds && restoredFilter.search) {
          restoredFilter.search = '';
        }
        this.filter = restoredFilter;
      } else if (storedState.currentCenter?.lat != null && storedState.currentCenter?.lng != null) {
        this.filter.origin_Lat = storedState.currentCenter.lat;
        this.filter.origin_long = storedState.currentCenter.lng;
        if (storedState.currentCenter.zoom != null) {
          this.filter.zoomLevel = storedState.currentCenter.zoom;
        }
      }
      this.isInitial = false;
    } else {
      return false;
    }
    if (storedState.filterDataObject?.selectedLocation) {
      const savedLocation = storedState.filterDataObject.selectedLocation;
      this.city = {
        city: savedLocation.address,
        lat: savedLocation.lat,
        lng: savedLocation.lng,
        enableZoomFallBack: true
      };
    }
    this.restoredFromReturnNavigation = hasFilters;
    return true;
  }

  private persistListingViewState() {
    if (this.tourListing) return;
    // Starting/resuming from an ordinary listing may navigate straight to a later
    // chapter. Capture its current context before that document is unloaded too.
    if (this.tour.active) this.tour.snapshotListing(this.originalListingSnapshot());
    this.businessService.saveListingState({
      activePanel: this.activePanel,
      gridView: this.gridView,
      currentCenter: this.currentCenter,
      searchIds: this.searchIds
    });
  }
}
